import { Link, Navigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import usePaginate from "@/components/paginate/usePaginate";
import { useQuery } from "@tanstack/react-query";
import { Stack } from "@altimateai/lego";
import { ArrowRightIcon } from "@assets/icons";
import { toast, Typography } from "@altimateai/lego";
import { getStoredProceduresChildQueries } from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { StoredProceduresChildQueriesListResponse, WorkloadType } from "@/pages/query/types";
import { formatBytes, formatCost, formatMilliseconds } from "@/helpers/utils";
import dayjs from "dayjs";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { useState, useEffect } from "react";
import { useAppState } from "@/modules/app/useAppContext";
import { useBreadcrumb, getEntityName } from "@/components/breadcrumbs";

export const StoredProceduresTaskHistory = () => {
  const { currency } = useAppState();
  const { queryId } = useParams();
  const { state } = useLocation();
  const { startDate, endDate } = useDateFromSearchParams();
  const { page, setPage, pageSize, updatePageSize } = usePaginate();
  const [searchParams] = useSearchParams();
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useBreadcrumb(getEntityName(state) ?? queryId ?? "Task History", {
    defaultParents: [
      { label: "Code", pathname: "/query" },
      { label: "Stored Procedures", pathname: "/query/stored-procedures" },
    ],
  });

  const tableHeaders = [
    {
      id: 1,
      label: "Task ID",
      uid: "task_id",
      render: (item: StoredProceduresChildQueriesListResponse) => {
        const url =
          item.query_type === WorkloadType.STORED_PROCEDURE
            ? `/query/stored-procedures/${item.hash}/${item.query_id}?run_query=${item.query_summary}&query_summary=${item.query_summary}`
            : `/query/${item.query_id}`;
        return (
          <Link to={url} className="al-text-brand condense-text cursor-pointer">
            {item.query_summary}
          </Link>
        );
      },
    },
    {
      id: 2,
      label: "Query Cost",
      uid: "query_cost",
      render: (item: StoredProceduresChildQueriesListResponse) => (
        <div>{formatCost(item.cost, currency)}</div>
      ),
    },
    {
      id: 3,
      label: "Bytes Scanned",
      uid: "bytes_scanned",
      render: (item: StoredProceduresChildQueriesListResponse) => (
        <div>{formatBytes(item.bytes_scanned)}</div>
      ),
    },
    {
      id: 4,
      label: "Execution Time",
      uid: "execution_time",
      render: (item: StoredProceduresChildQueriesListResponse) => (
        <div>{formatMilliseconds(item.execution_time)}</div>
      ),
    },
    {
      id: 5,
      label: "Warehouse",
      uid: "warehouse",
      render: (item: StoredProceduresChildQueriesListResponse) => <div>{item.warehouse_name}</div>,
    },
    {
      id: 6,
      label: "Timestamp",
      uid: "start_time",
      render: (item: StoredProceduresChildQueriesListResponse) => (
        <div>{dayjs(item.start_time).format("YYYY-MM-DD hh:mm a")}</div>
      ),
    },
    {
      id: 7,
      label: "Status",
      uid: "execution_status",
      render: (item: StoredProceduresChildQueriesListResponse) => (
        <span>{item.execution_status}</span>
      ),
    },
  ];

  const { data, isLoading } = useQuery({
    queryKey: ["stored_procedures_tasks", queryId, startDate, endDate, page, pageSize],
    queryFn: () =>
      getStoredProceduresChildQueries(queryId!, {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        page: page + 1,
        size: pageSize,
      }),
    enabled: Boolean(queryId),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (data) {
      setTotalItems(data?.total ?? 0);
      setTotalPages(data?.pages ?? 0);
    }
  }, [data]);

  if (!queryId) {
    return <Navigate to="/query/stored-procedures" />;
  }
  if (!isLoading && !data) {
    toast({
      variant: "destructive",
      description: "No child queries found for this stored procedure execution.",
    });
    return <Navigate to="/query/stored-procedures" />;
  }
  return (
    <Stack direction="column" gap={"xl"}>
      <Stack align={"center"} gap={"sm"}>
        <Typography variant={"section-heading"} className={"al-text-light-gray"}>
          Child Queries
        </Typography>
        <div className="al-size-5 al-text-light-gray">
          <ArrowRightIcon />
        </div>
        <span className="al-text-xl">{searchParams.get("run_query") || queryId}</span>
      </Stack>
      <Stack direction={"column"} gap={"lg"}>
        <DataTable
          isLoading={isLoading}
          header={tableHeaders}
          data={{ items: data?.items ?? [] }}
        />
        <Paginate
          isLoading={isLoading}
          itemCount={totalItems}
          numPages={totalPages}
          page={page}
          pageSize={pageSize}
          onPageClick={setPage}
          onPageSizeChange={updatePageSize}
          showPageSize
        />
      </Stack>
    </Stack>
  );
};
